// Просмотр документа справа от чата (образец владельца — «Юрист Гуру»): «имя · тип», «Скачать», печать, развернуть,
// закрыть. Развёрнутый просмотр — во всю ширину, в верхней строке вкладки и «Свернуть». На телефоне — во весь экран.
// PDF рисует pdf.js, Word — docx-preview: обе библиотеки грузятся, только когда такой файл открыли.
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { api, downloadDocument, getJson } from "../api";
import { boot, type Paragraph } from "../boot";
import { routeStore } from "../router";
import { savedStore, Store, uploadedStore, useStore, type SavedItem, type UploadedDoc } from "../store";
import { DocPage } from "./DocPage";
import { Icon } from "./Icon";
import { workspaceStore } from "./Topbar";

const PdfView = lazy(() => import("./viewer/PdfView"));
const DocxView = lazy(() => import("./viewer/DocxView"));

export type ViewerTarget = { kind: "upload"; id: string } | { kind: "saved"; id: string };
type ViewerState = { target: ViewerTarget | null; expanded: boolean };
export const viewerStore = new Store<ViewerState>({ target: null, expanded: false });

export function openViewer(target: ViewerTarget) {
  document.body.classList.remove("nav-open");            // на телефоне меню закрывается: документ — во весь экран
  viewerStore.set({ target, expanded: false });
}
export const closeViewer = () => viewerStore.set({ target: null, expanded: false });
export const expandViewer = (expanded: boolean) => viewerStore.update((state) => ({ ...state, expanded: Boolean(state.target) && expanded }));

const IMAGES = new Set([".jpg", ".jpeg", ".png"]);
const extOf = (doc: UploadedDoc) => (doc.ext || doc.filename.slice(doc.filename.lastIndexOf("."))).toLowerCase();

type Shown = { name: string; type: string; doc?: UploadedDoc; item?: SavedItem };

// Открытый документ — по свежим спискам: переименовали — новое имя, удалили — просмотр закрывается.
export function useShown(): Shown | null {
  const { target } = useStore(viewerStore);
  const uploaded = useStore(uploadedStore);
  const saved = useStore(savedStore);
  if (target?.kind === "upload") {
    const doc = uploaded.find((one) => one.id === target.id);
    return doc ? { name: doc.filename, type: extOf(doc).replace(".", "").toUpperCase() || "Файл", doc } : null;
  }
  if (target?.kind === "saved") {
    const item = saved.doc.find((one) => one.id === target.id);
    return item ? { name: item.title, type: "Word", item } : null;
  }
  return null;
}

// ---------------------------------------------------------------- печать

// Печать через невидимую рамку: страница сайта остаётся как есть, на бумагу идёт только документ.
function printFrame(setup: (frame: HTMLIFrameElement) => void) {
  const frame = document.createElement("iframe");
  frame.className = "print-frame";
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.addEventListener("load", () => {
    const view = frame.contentWindow;
    if (!view) return;
    setTimeout(() => {
      view.focus();
      view.print();
      setTimeout(() => frame.remove(), 60_000);             // окно печати ещё может читать документ
    }, 200);
  }, { once: true });
  setup(frame);
  document.body.append(frame);
}

const SAFARI = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

function printPdf(data: ArrayBuffer) {
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  setTimeout(() => URL.revokeObjectURL(url), 120_000);
  if (SAFARI) { window.open(url, "_blank"); return; }       // Safari печатает PDF из рамки пустым листом
  printFrame((frame) => { frame.src = url; });
}

const PRINT_CSS: Record<string, string> = {
  docx: "@page { margin: 0 } .docx-wrapper { background: none !important; padding: 0 !important; zoom: 1 !important }"
    + " .docx-wrapper > section.docx { box-shadow: none !important; margin: 0 auto !important; break-after: page }",
  sheet: "@page { margin: 20mm } .doc-page { width: auto !important; padding: 0 !important }"
    + " .doc-page mark { background: none !important; padding: 0 !important }",
  text: "@page { margin: 20mm } pre { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font: 12pt/1.5 \"Times New Roman\", serif }",
  image: "@page { margin: 10mm } img { display: block; max-width: 100%; max-height: 95vh; margin: 0 auto; box-shadow: none !important }",
};
// Лист в просмотре — с тенью и полями на сером фоне; на бумаге — просто текст.
const PAPER = ".viewer-page { box-shadow: none !important; max-width: none !important; padding: 0 !important }";

function printHtml(html: string, kind: string, title: string) {
  printFrame((frame) => {
    frame.srcdoc = `<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>${title.replace(/</g, "&lt;")}</title>`
      + `<link rel="stylesheet" href="/static/style.css?v=${boot.version}">`
      + `<style>html, body { margin: 0; background: #fff } ${PAPER} ${PRINT_CSS[kind]}</style></head><body>${html}</body></html>`;
  });
}

// ---------------------------------------------------------------- содержимое

const decode = (data: ArrayBuffer) => {
  try { return new TextDecoder("utf-8", { fatal: true }).decode(data); } catch { return new TextDecoder("windows-1251").decode(data); }
};

function Message({ children }: { children: ReactNode }) {
  return <div className="viewer-message" role="status">{children}</div>;
}

function SavedSheet({ item }: { item: SavedItem }) {
  const [paragraphs, setParagraphs] = useState<Paragraph[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    getJson<Paragraph[]>(`/api/saved/${item.id}/preview`).then((found) => { if (alive) setParagraphs(found); },
      (problem) => { if (alive) setError((problem as Error).message); });
    return () => { alive = false; };
  }, [item.id, item.payload]);
  if (error) return <Message>{error}</Message>;
  if (!paragraphs) return <Message>Открываю документ…</Message>;
  return <div className="viewer-pages" data-print="sheet"><div className="viewer-page viewer-sheet"><DocPage paragraphs={paragraphs} /></div></div>;
}

function UploadContent({ doc, data, error, onError }: { doc: UploadedDoc; data: ArrayBuffer | null; error: string; onError: (text: string) => void }) {
  const ext = extOf(doc);
  if (IMAGES.has(ext)) {
    return <div className="viewer-pages" data-print="image"><img className="viewer-image" src={`/api/documents/${doc.id}/file`} alt={doc.filename} /></div>;
  }
  if (error) return <Message>{error}</Message>;
  if (!data) return <Message>Открываю документ…</Message>;
  if (ext === ".pdf") return <PdfView data={data} onError={onError} />;
  if (ext === ".docx" || ext === ".docm") return <DocxView data={data} onError={onError} />;
  return <div className="viewer-pages" data-print="text"><div className="viewer-page viewer-text"><pre>{decode(data)}</pre></div></div>;
}

// ---------------------------------------------------------------- панель

export function DocViewer() {
  const { target, expanded } = useStore(viewerStore);
  const shown = useShown();
  const [file, setFile] = useState<{ id: string; data: ArrayBuffer | null; error: string } | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLElement>(null);
  const doc = shown?.doc;
  const needsFile = doc && !IMAGES.has(extOf(doc));

  const open = Boolean(shown);
  useEffect(() => { if (target && !open) closeViewer(); }, [target, open]);   // документ удалили
  useEffect(() => {
    document.body.classList.toggle("viewer-open", open);
    document.body.classList.toggle("viewer-expanded", open && expanded);
  }, [open, expanded]);
  useEffect(() => () => { document.body.classList.remove("viewer-open", "viewer-expanded"); }, []);

  // Файл скачиваем один раз на открытие: его же рисует просмотр и печатает PDF.
  const docId = needsFile ? doc.id : null;
  useEffect(() => {
    if (!docId) { setFile(null); return; }
    let alive = true;
    setFile({ id: docId, data: null, error: "" });
    api(`/api/documents/${docId}/file`).then((response) => response.arrayBuffer())
      .then((data) => { if (alive) setFile({ id: docId, data, error: "" }); },
        (problem) => { if (alive) setFile({ id: docId, data: null, error: (problem as Error).message }); });
    return () => { alive = false; };
  }, [docId]);

  const targetKey = target ? `${target.kind}:${target.id}` : "";
  useEffect(() => { if (targetKey) panel.current?.focus({ preventScroll: true }); }, [targetKey]);
  useEffect(() => { body.current?.scrollTo(0, 0); }, [targetKey]);

  if (!shown || !target) return null;
  const data = file && file.id === doc?.id ? file : null;

  function print() {
    if (!shown) return;
    if (doc && extOf(doc) === ".pdf") {
      if (data?.data) printPdf(data.data);
      return;
    }
    const content = body.current?.querySelector<HTMLElement>("[data-print]");
    if (content) printHtml(content.innerHTML, content.dataset.print || "text", shown.name);
  }

  async function download() {
    if (!shown?.item) return;
    try { await downloadDocument(shown.item.payload.slug, shown.item.payload.answers); } catch (error) { alert((error as Error).message); }
  }

  return (
    <section ref={panel} className="viewer" tabIndex={-1} aria-label={`Просмотр: ${shown.name}`}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.preventDefault();
        if (expanded) expandViewer(false); else closeViewer();
      }}>
      <header className="viewer-head">
        <p className="viewer-name" title={`${shown.name} · ${shown.type}`}>{shown.name}<span className="viewer-type"> · {shown.type}</span></p>
        <div className="viewer-actions">
          {doc ? (
            <a className="viewer-download" href={`/api/documents/${doc.id}/file`} download={doc.filename}><Icon name="download" />Скачать</a>
          ) : (
            <button type="button" className="viewer-download" onClick={download}><Icon name="download" />Скачать</button>
          )}
          <button type="button" className="viewer-round" aria-label="Печать" title="Печать" disabled={Boolean(needsFile && !data?.data)} onClick={print}>
            <Icon name="print" />
          </button>
          {!expanded && (
            <button type="button" className="viewer-round viewer-expand" aria-label="Развернуть" title="Развернуть" onClick={() => expandViewer(true)}>
              <Icon name="expand" />
            </button>
          )}
          <button type="button" className="viewer-round" aria-label="Закрыть просмотр" title="Закрыть" onClick={closeViewer}><Icon name="close" /></button>
        </div>
      </header>
      <div ref={body} className="viewer-body">
        <Suspense fallback={<Message>Открываю документ…</Message>}>
          {doc ? (
            <UploadContent key={doc.id} doc={doc} data={data?.data || null} error={data?.error || ""}
              onError={(text) => setFile({ id: doc.id, data: null, error: text })} />
          ) : <SavedSheet key={shown.item!.id} item={shown.item!} />}
        </Suspense>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- вкладки в верхней строке (развёрнутый просмотр)

const SECTIONS: Record<string, string> = {
  my: "Мои документы", documents: "Шаблоны документов", dashboard: "Дашборд", settings: "Настройки", privacy: "Политика конфиденциальности", admin: "Администратор",
};

export function ViewerTabs() {
  const shown = useShown();
  const workspace = useStore(workspaceStore);
  const { page } = useStore(routeStore);
  if (!shown) return null;
  const chat = page === "chat" ? (workspace && workspace.kind !== "home" ? workspace.title : "Новый чат") : SECTIONS[page] || "Правосвод";
  return (
    <nav className="viewer-tabs" aria-label="Вкладки">
      <button type="button" className="viewer-tab" title="Вернуться к чату рядом с документом" onClick={() => expandViewer(false)}>
        <Icon name={page === "chat" ? "chat" : "library"} /><span>{chat}</span>
      </button>
      <span className="viewer-tab is-active" aria-current="page" title={shown.name}><Icon name="doc" /><span>{shown.name}</span></span>
    </nav>
  );
}
